import { useEffect, useMemo, useState } from "react";
import { parseSync } from "@loaders.gl/core";
import { WKTLoader } from "@loaders.gl/wkt";
import { Area } from "ais-facade-api/api";
import { useGetAnchorageMutation } from "app/api/facade";
import { GeoJsonLayer, IconLayer } from "deck.gl/typed";
import { svgToDataURL } from "app/helpers";
import { useAppDispatch, useAppSelector } from "app/store/store";
import { setAnchorageOnMap } from "app/utils/facadeReducer";

export const useLayerAnchorage = (viewState: any, showAnchorage: boolean) => {
  const dispatch = useAppDispatch();
  const mode = useAppSelector((state) => state.mapStateSlice.mode);
  const anchorages = useAppSelector((state) => state.facadeSlice.anchoragesOnMap);
  const isAnalytics = mode === 'analytics';

  const [, { data: anchorageResponse, ...getAnchorageState }] = useGetAnchorageMutation({
    fixedCacheKey: 'get-map-anchorage'
  });

  const [anchorageData, setAnchorageData] = useState<Area[]>([]);

  const calculateCenter = (vertices: number[][]) => {
    const sumLat = vertices.reduce((acc: any, coord: any[]) => acc + coord[1], 0);
    const sumLng = vertices.reduce((acc: any, coord: any[]) => acc + coord[0], 0);
    const centerLat = sumLat / vertices.length;
    const centerLng = sumLng / vertices.length;
    return { centerLng, centerLat };
  };

  const generateGeoJsonLayer = (area: Area, index: number, layerIdPrefix: string, visibleCondition: boolean) => {
    return new GeoJsonLayer({
      id: `${layerIdPrefix}-${index}`,
      data: area.geometry,
      filled: true,
      stroked: true,
      getFillColor: () => [193, 26, 199, 80],
      getLineColor: () => [248, 59, 255, 50],
      getLineWidth: 2,
      lineWidthUnits: 'pixels',
      visible: visibleCondition,
    });
  };

  const centerPoint = (area: any, index: number, layerIdPrefix: string, visibleCondition: boolean) => {
    return new IconLayer({
      id: `${layerIdPrefix}-${index}`,
      data: area.center,
      pickable: false,
      getPosition: (d: any) => [d.centerLng, d.centerLat, 0],
      getIcon: () => ({
        url: svgToDataURL(`<svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
<path d="M12 21.1704C11.8199 20.9279 11.5858 20.608 11.3136 20.2257C10.6621 19.3105 9.79417 18.0395 8.9271 16.615C8.05912 15.1891 7.19738 13.618 6.55404 12.1016C5.90652 10.5753 5.5 9.14927 5.5 8C5.5 6.60003 5.96925 4.96606 7.01198 3.69162C8.04194 2.43278 9.64985 1.5 12 1.5C14.3502 1.5 15.9581 2.43278 16.988 3.69162C18.0307 4.96606 18.5 6.60003 18.5 8C18.5 9.14927 18.0935 10.5753 17.446 12.1016C16.8026 13.618 15.9409 15.1891 15.0729 16.615C14.2058 18.0395 13.3379 19.3105 12.6864 20.2257C12.4142 20.608 12.1801 20.9279 12 21.1704Z" fill="white" stroke="black"/>
<path d="M13 4H11V12H13V4Z" fill="black"/>
<path d="M15 6H9V7H15V6Z" fill="black"/>
<path d="M15.6 9C14.8391 10.6 13.5761 11.6667 12 11.6667C10.4239 11.6667 9.16087 10.6 8.4 9H7C7.81522 11.3333 9.77174 13 12 13C14.2283 13 16.1304 11.3333 17 9H15.6Z" fill="black"/>
    </svg>`),
        width: 24,
        height: 24,
      }),
      visible: visibleCondition,
      sizeMinPixels: 24,
      updateTriggers: {
        getIcon: [viewState, area],
      },
      sizeScale: 10,
    })
  }

  useEffect(() => {
    if (anchorageResponse?.results && getAnchorageState.isSuccess) {
      dispatch(setAnchorageOnMap(anchorageResponse.results));
    }
  }, [anchorageResponse]);

  useEffect(() => {
    setAnchorageData([]);
    anchorages?.forEach((area: Area) => {
      if (area?.geometry) {
        const coordinates = parseSync(area.geometry, WKTLoader);
        const vertices = coordinates.coordinates[0];
        const center = calculateCenter(vertices);

        setAnchorageData((prevAreaData: Area[]) => [
          ...prevAreaData,
          {
            geometry: coordinates,
            color: area.color,
            center: [center],
          },
        ]);
      }
    });


  }, [anchorages])

  const layerAnchorage = anchorageData.map((area, index) => generateGeoJsonLayer(area, index, 'polygon-layer-anchorage', viewState.zoom > 6 && showAnchorage && !isAnalytics));
  const layerPoint = anchorageData.map((area, index) => centerPoint(area, index, 'point-layer-anchorage', viewState.zoom > 6 && showAnchorage && !isAnalytics));

  const result = useMemo(
    () => ({
      layer: [...layerAnchorage, ...layerPoint],
      data: anchorageData,
      isLoading: getAnchorageState.isLoading,
      visible: viewState.zoom > 6 && showAnchorage && !isAnalytics,
    }),
    [layerAnchorage],
  );
  return result;
}